import React, { useState } from "react";

export function PoiDeliveredChart({ data, targetVal }) {
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const height = 260;
  const width = 500;
  const paddingLeft = 45;
  const paddingRight = 20;
  const paddingTop = 20;
  const paddingBottom = 40;

  const chartWidth = width - paddingLeft - paddingRight;
  const chartHeight = height - paddingTop - paddingBottom;

  if (!data || data.length === 0) return null;

  const values = data.flatMap((d) => [
    parseFloat(d.active_fleet?.poi_mwh) || 0,
    parseFloat(d.contracted_poi_mwh) || 0,
  ]);
  const minVal = 0;
  const maxVal = Math.max(...values, (targetVal || 0) * 1.1) || 120;

  const getX = (index) => {
    return paddingLeft + (index + 0.5) * (chartWidth / data.length);
  };

  const getY = (val) => {
    return paddingTop + chartHeight - ((val - minVal) / (maxVal - minVal)) * chartHeight;
  };

  const targetY = getY(targetVal || 0);
  const targetPath = `M ${paddingLeft} ${targetY} L ${width - paddingRight} ${targetY}`;

  const yTicks = [
    0,
    Math.round(maxVal * 0.25),
    Math.round(maxVal * 0.5),
    Math.round(maxVal * 0.75),
    Math.round(maxVal),
  ];

  const barWidth = Math.max(4, (chartWidth / data.length) * 0.6);

  return (
    <div className="relative bg-white p-2 h-full flex flex-col justify-between">
      <h5 className="text-[12px] font-bold text-slate-700 mb-3 flex items-center justify-between">
        <span>POI Energy vs Contract Target (MWh)</span>
        <div className="flex gap-3 text-[9px] font-semibold">
          <span className="flex items-center gap-1 text-red-500">
            <span className="w-2.5 h-0.5 bg-red-500 inline-block border-t border-red-500 border-dashed" />
            Target ({(targetVal || 0).toFixed(1)} MWh)
          </span>
          <span className="flex items-center gap-1 text-sky-500">
            <span className="w-3.5 h-2 bg-sky-500 rounded-sm inline-block" />
            POI Delivered
          </span>
        </div>
      </h5>

      <svg viewBox={`0 0 ${width} ${height}`} className="w-full flex-1 min-h-[180px]">
        {yTicks.map((tick, i) => {
          const y = getY(tick);
          return (
            <g key={i} className="opacity-40">
              <line
                x1={paddingLeft}
                y1={y}
                x2={width - paddingRight}
                y2={y}
                stroke="#cbd5e1"
                strokeWidth="1"
                strokeDasharray="4 4"
              />
              <text x={paddingLeft - 8} y={y + 3} textAnchor="end" className="text-[8px] font-bold fill-slate-400">
                {tick}
              </text>
            </g>
          );
        })}

        {data.map((d, i) => {
          const x = getX(i);
          return (
            <text
              key={i}
              x={x}
              y={height - paddingBottom + 16}
              textAnchor="middle"
              className="text-[7.5px] font-bold fill-slate-400"
            >
              {d.project_year}
            </text>
          );
        })}

        {/* Render bars for POI Delivered */}
        {data.map((d, i) => {
          const x = getX(i);
          const val = parseFloat(d.active_fleet?.poi_mwh || 0);
          if (val <= 0) return null;

          const barHeight = (val / maxVal) * chartHeight;
          const y = getY(val);

          return (
            <rect
              key={i}
              x={x - barWidth / 2}
              y={y}
              width={barWidth}
              height={barHeight}
              fill={hoveredIndex === i ? "#0ea5e9" : "#0284c7"}
              rx="2"
              className="transition-all duration-200"
            />
          );
        })}

        {/* Target Line */}
        <path d={targetPath} fill="none" stroke="#EF4444" strokeWidth="1.5" strokeDasharray="5 5" />

        {/* Hover zones */}
        {data.map((d, i) => {
          const x = getX(i);
          return (
            <g key={i}>
              <rect
                x={x - chartWidth / data.length / 2}
                y={paddingTop}
                width={chartWidth / data.length}
                height={chartHeight}
                fill="transparent"
                className="cursor-pointer"
                onMouseEnter={() => setHoveredIndex(i)}
                onMouseLeave={() => setHoveredIndex(null)}
              />
              {hoveredIndex === i && (
                <line
                  x1={x}
                  y1={paddingTop}
                  x2={x}
                  y2={height - paddingBottom}
                  stroke="#94a3b8"
                  strokeWidth="1"
                  strokeDasharray="2 2"
                />
              )}
            </g>
          );
        })}
      </svg>

      {hoveredIndex !== null && data[hoveredIndex] && (
        <div className="absolute top-[65px] left-[55px] bg-slate-900/90 text-white rounded-lg p-2 text-[9px] space-y-0.5 shadow-lg pointer-events-none backdrop-blur-sm z-30 min-w-[120px]">
          <div className="font-bold text-slate-300 border-b border-slate-700 pb-0.5 mb-1">
            Project Year {data[hoveredIndex].project_year}
          </div>
          <div>
            POI: <span className="font-bold text-sky-400">{parseFloat(data[hoveredIndex].active_fleet?.poi_mwh || 0).toFixed(2)} MWh</span>
          </div>
          <div>
            Target: <span className="font-bold text-red-400">{parseFloat(data[hoveredIndex].contracted_poi_mwh || 0).toFixed(2)} MWh</span>
          </div>
          <div>
            Margin:{" "}
            <span
              className={`font-bold ${
                parseFloat(data[hoveredIndex].active_fleet?.poi_margin_mwh || 0) < 0
                  ? "text-red-400"
                  : "text-emerald-400"
              }`}
            >
              {parseFloat(data[hoveredIndex].active_fleet?.poi_margin_mwh || 0).toFixed(2)} MWh
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

export function AugmentationChart({ data }) {
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const height = 260;
  const width = 500;
  const paddingLeft = 45;
  const paddingRight = 20;
  const paddingTop = 20;
  const paddingBottom = 40;

  const chartWidth = width - paddingLeft - paddingRight;
  const chartHeight = height - paddingTop - paddingBottom;

  if (!data || data.length === 0) return null;

  const values = data.map((d) => parseInt(d.augmented_fleet?.newly_purchased || 0, 10));
  const maxVal = Math.max(...values, 5) || 5;

  const getX = (index) => {
    return paddingLeft + (index + 0.5) * (chartWidth / data.length);
  };

  const getY = (val) => {
    return paddingTop + chartHeight - (val / maxVal) * chartHeight;
  };

  const yTicks = [
    0,
    Math.round(maxVal * 0.25),
    Math.round(maxVal * 0.5),
    Math.round(maxVal * 0.75),
    Math.round(maxVal),
  ];

  const barWidth = Math.max(4, (chartWidth / data.length) * 0.6);

  return (
    <div className="relative bg-white p-2 h-full flex flex-col justify-between">
      <h5 className="text-[12px] font-bold text-slate-700 mb-3 flex items-center justify-between">
        <span>Augmentation Schedule (Containers Purchased)</span>
        <div className="flex gap-2 text-[9px] font-semibold">
          <span className="flex items-center gap-1 text-amber-500">
            <span className="w-3.5 h-2 bg-amber-500 rounded-sm inline-block" />
            Containers Added
          </span>
        </div>
      </h5>

      <svg viewBox={`0 0 ${width} ${height}`} className="w-full flex-1 min-h-[180px]">
        {yTicks.map((tick, i) => {
          const y = getY(tick);
          return (
            <g key={i} className="opacity-40">
              <line
                x1={paddingLeft}
                y1={y}
                x2={width - paddingRight}
                y2={y}
                stroke="#cbd5e1"
                strokeWidth="1"
                strokeDasharray="4 4"
              />
              <text x={paddingLeft - 8} y={y + 3} textAnchor="end" className="text-[8px] font-bold fill-slate-400">
                {tick}
              </text>
            </g>
          );
        })}

        {data.map((d, i) => {
          const x = getX(i);
          return (
            <text
              key={i}
              x={x}
              y={height - paddingBottom + 16}
              textAnchor="middle"
              className="text-[7.5px] font-bold fill-slate-400"
            >
              {d.project_year}
            </text>
          );
        })}

        {/* Render bars for years with augmentation */}
        {data.map((d, i) => {
          const x = getX(i);
          const qty = parseInt(d.augmented_fleet?.newly_purchased || 0, 10);
          if (qty <= 0) return null;

          const barHeight = (qty / maxVal) * chartHeight;
          const y = getY(qty);

          return (
            <rect
              key={i}
              x={x - barWidth / 2}
              y={y}
              width={barWidth}
              height={barHeight}
              fill="#f59e0b"
              rx="2"
              className="transition-all duration-200"
            />
          );
        })}

        {/* Hover zones */}
        {data.map((d, i) => {
          const x = getX(i);
          return (
            <g key={i}>
              <rect
                x={x - chartWidth / data.length / 2}
                y={paddingTop}
                width={chartWidth / data.length}
                height={chartHeight}
                fill="transparent"
                className="cursor-pointer"
                onMouseEnter={() => setHoveredIndex(i)}
                onMouseLeave={() => setHoveredIndex(null)}
              />
              {hoveredIndex === i && (
                <line
                  x1={x}
                  y1={paddingTop}
                  x2={x}
                  y2={height - paddingBottom}
                  stroke="#94a3b8"
                  strokeWidth="1"
                  strokeDasharray="2 2"
                />
              )}
            </g>
          );
        })}
      </svg>

      {hoveredIndex !== null && data[hoveredIndex] && (
        <div className="absolute top-[65px] left-[55px] bg-slate-900/90 text-white rounded-lg p-2 text-[9px] space-y-0.5 shadow-lg pointer-events-none backdrop-blur-sm z-30 min-w-[120px]">
          <div className="font-bold text-slate-300 border-b border-slate-700 pb-0.5 mb-1">
            Project Year {data[hoveredIndex].project_year}
          </div>
          <div>
            Newly Purchased:{" "}
            <span className="font-bold text-amber-400">
              {parseInt(data[hoveredIndex].augmented_fleet?.newly_purchased || 0, 10)} Containers
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
